import React, { useEffect, useRef } from 'react';
import anime from 'animejs';
import { SpeedUnit } from '../../types';

interface SpeedometerProps {
  speed: number;
  speedUnit: SpeedUnit;
  acceleration: number;
  maxSpeed?: number;
}

export const EvComfortSpeedometer: React.FC<SpeedometerProps> = ({
  speed,
  speedUnit,
  maxSpeed = 240,
}) => {
  const waveRingRef = useRef<SVGCircleElement | null>(null);
  const speedTextRef = useRef<HTMLSpanElement | null>(null);
  const prevSpeedRef = useRef<number>(0);

  const clampedSpeed = Math.max(0, Math.min(maxSpeed, speed));
  const speedRatio = clampedSpeed / maxSpeed;

  useEffect(() => {
    if (!speedTextRef.current) return;
    const obj = { val: prevSpeedRef.current };
    anime({
      targets: obj,
      val: speed,
      round: 1,
      duration: 500,
      easing: 'easeOutQuart',
      update: () => {
        if (speedTextRef.current) {
          speedTextRef.current.textContent = String(Math.round(obj.val));
        }
      },
    });
    prevSpeedRef.current = speed;
  }, [speed]);

  useEffect(() => {
    if (!waveRingRef.current) return;
    anime({
      targets: waveRingRef.current,
      r: [120, 126, 120],
      strokeWidth: [2.5, 4, 2.5],
      duration: 4000,
      easing: 'easeInOutSine',
      loop: true,
    });
  }, []);

  return (
    <div className="relative w-76 h-76 md:w-88 md:h-88 flex flex-col items-center justify-center select-none">
      {/* Floating Cyan/Blue Acoustic Glow */}
      <div
        className="absolute inset-8 rounded-full blur-3xl pointer-events-none transition-all duration-300"
        style={{
          background: 'radial-gradient(circle, rgba(6,182,212,0.25) 0%, rgba(59,130,246,0.1) 65%, transparent 100%)',
          transform: `scale(${1.0 + speedRatio * 0.4})`,
        }}
      />

      {/* Floating Concentric Harmonic Rings */}
      <svg className="w-full h-full" viewBox="0 0 300 300">
        <circle
          cx="150"
          cy="150"
          r="138"
          fill="none"
          stroke="rgba(6, 182, 212, 0.15)"
          strokeWidth="1"
        />
        <circle
          ref={waveRingRef}
          cx="150"
          cy="150"
          r="120"
          fill="none"
          stroke="#06b6d4"
          strokeWidth="2.5"
          strokeDasharray="8 6"
          className="filter drop-shadow-[0_0_10px_rgba(6,182,212,0.6)]"
        />
        <circle
          cx="150"
          cy="150"
          r="105"
          fill="none"
          stroke="rgba(148, 163, 184, 0.1)"
          strokeWidth="1"
        />
      </svg>

      {/* Center Spacious Luxury Content */}
      <div className="absolute inset-0 flex flex-col items-center justify-center text-center">
        <span className="text-[10px] font-mono tracking-[0.3em] text-cyan-400 font-bold uppercase mb-1">
          SANCTUARY HARMONICS
        </span>
        <span
          ref={speedTextRef}
          className="text-7xl md:text-8xl font-black font-display text-white tracking-tight drop-shadow-[0_4px_16px_rgba(6,182,212,0.4)] my-1"
        >
          {Math.round(speed)}
        </span>
        <span className="text-xs font-mono tracking-widest text-cyan-400 font-black uppercase">
          {speedUnit.toUpperCase()}
        </span>
        <span className="text-[9px] font-mono text-slate-400 tracking-wider uppercase mt-2">
          CONTINUOUS PROPULSION
        </span>
      </div>
    </div>
  );
};
